import { useCallback, useEffect, useMemo, useState } from 'react';
import SeasonalBorder from '@/components/overlays/SeasonalBorder';
import { Section, useBearer, useToast } from './_admin-utils';

const DEFAULT_SETTINGS = {
  enabled: false,
  pack: 'auto',
  intensity: 'med',
  border: 'none',
};

const PACK_OPTIONS = [
  { value: 'auto', label: 'Automatic — follow calendar' },
  { value: 'none', label: 'None (disable overlay)' },
  { value: 'fall', label: 'Falling leaves' },
  { value: 'winter', label: 'Snowfall' },
  { value: 'lights', label: 'Holiday lights' },
  { value: 'confetti', label: 'Confetti celebration' },
  { value: 'hearts', label: 'Hearts (appreciation)' },
  { value: 'halloween', label: 'Halloween bats' },
];

const INTENSITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const BORDER_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'aurora', label: 'Aurora glow' },
  { value: 'frost', label: 'Frosted glass' },
  { value: 'harvest', label: 'Harvest garland' },
  { value: 'sparkle', label: 'Sparkle halo' },
];

function normalizePayload(data: unknown) {
  if (!data || typeof data !== 'object') return { ...DEFAULT_SETTINGS };
  const record = data as Record<string, unknown>;
  return {
    enabled: typeof record.enabled === 'boolean' ? record.enabled : DEFAULT_SETTINGS.enabled,
    pack: typeof record.pack === 'string' ? record.pack : DEFAULT_SETTINGS.pack,
    intensity: typeof record.intensity === 'string' ? record.intensity : DEFAULT_SETTINGS.intensity,
    border: typeof record.border === 'string' ? record.border : DEFAULT_SETTINGS.border,
  };
}

export default function SeasonalDecor() {
  const bearer = useBearer();
  const { show, ToastHost } = useToast();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/community/seasonal-settings`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load settings');
      }
      setSettings(normalizePayload(payload));
    } catch (err: unknown) {
      setError((err as Error | null)?.message || 'Unable to load settings');
      setSettings({ ...DEFAULT_SETTINGS });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (key: string, value: unknown) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!bearer) {
      setError('Not authenticated');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const CORE_API_POST = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API_POST}/api/v1/admin/seasonal-settings`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...bearer },
        body: JSON.stringify(settings),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to update settings');
      }
      setSettings(normalizePayload(payload));
      show('Seasonal decor updated');
    } catch (err: unknown) {
      setError((err as Error | null)?.message || 'Unable to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...DEFAULT_SETTINGS });
  };

  const previewTitle = useMemo(() => {
    const packLabel = PACK_OPTIONS.find((option) => option.value === settings.pack)?.label || 'Automatic';
    const borderLabel = BORDER_OPTIONS.find((option) => option.value === settings.border)?.label || 'None';
    return `${packLabel} • ${borderLabel}`;
  }, [settings.pack, settings.border]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Seasonal Decor</h1>
        <p className="text-sm text-cocoa/70 max-w-2xl">
          Control the animated overlay and frame that appear across the public site, keeping seasonal celebrations in sync with your launch moments.
        </p>
      </header>

      <Section title="Live appearance" actions={null}>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex items-center gap-3 text-sm text-cocoa font-medium">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(event) => handleChange('enabled', event.target.checked)}
              />
              Enable overlay animation
            </label>
            <label className="text-sm text-cocoa font-medium">
              Overlay pack
              <select
                className="mt-1 w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                value={settings.pack}
                onChange={(event) => handleChange('pack', event.target.value)}
              >
                {PACK_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm text-cocoa font-medium">
              Intensity
              <select
                className="mt-1 w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                value={settings.intensity}
                onChange={(event) => handleChange('intensity', event.target.value)}
              >
                {INTENSITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-cocoa font-medium">
              Border animation
              <select
                className="mt-1 w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                value={settings.border}
                onChange={(event) => handleChange('border', event.target.value)}
              >
                {BORDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error ? <p className="text-sm text-brick">{error}</p> : null}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full border border-shell bg-white px-4 py-2 text-sm font-semibold uppercase tracking-wide text-cocoa shadow-soft transition hover:bg-shell/40 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
            >
              Reset to defaults
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell disabled:opacity-60"
            >
              Refresh
            </button>
            <span className="text-xs uppercase tracking-wide text-cocoa/60">
              {loading ? 'Loading settings…' : 'Preview updates instantly across the site'}
            </span>
          </div>
        </form>

        <div className="mt-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Preview</h3>
          <div className="relative mt-2 overflow-hidden rounded-3xl border border-white/70 bg-shell/40 p-6">
            <SeasonalBorder variant={settings.border} intensity={settings.intensity} embedded />
            <div className="relative z-10 mx-auto flex h-48 max-w-xl flex-col items-center justify-center text-center text-sm text-cocoa">
              <div className="rounded-full border border-white/60 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa/70">
                {previewTitle}
              </div>
              <p className="mt-3 max-w-sm text-cocoa/80">
                {settings.enabled
                  ? 'Falling elements and the seasonal frame will render across public surfaces once saved.'
                  : 'Overlay animation is disabled. Only the frame will show if enabled.'}
              </p>
            </div>
          </div>
        </div>
      </Section>

      <ToastHost />
    </div>
  );
}
